import { SolitaireGame } from 'game/SolitaireGame';
import { HintItem, HintType } from './hintGroup';
import { Card } from 'components/Card';
import { GameBoardPlace } from 'game/GameBoardPlace';
import { Column } from 'game/Column';
import { ACES_VALUE } from 'constants/cards';

// ======== The priority of the available moves shows in order: =========

// 1. Moving Cards to the Foundation (starting with the lowest-ranked card if other options are available).
// 2. Moving cards on the tableau to reveal cards that can be transferred to the Foundation.
//    or to freecell

// 3. Transfer cards to the correct sequence (a sequence that is uninterrupted and can be moved as a whole). Prioritize forming larger sequences (don't split already created sequences)
//    - correct sequence is in a whole column (no other cards in the column)

// 4. Moving cards from the FreeCell area to the tableau onto a smaller sequence.
// 5. Creating smaller sequences (starting with those that will form smaller sequences). Do not destroy the correct sequences. // -
// (cards in the columns are not complete in order)

// 6. Moving cards to the placeholder to create a larger sequence on the next move (prioritizing smaller cards first).
// 7. Moving cards to FreeCells to enable sequence creation.
// 8. Filling an empty column (starting with the largest card from left to right, prioritizing FreeCells first and considering the number of cards in the stack — the larger the stack, the higher the priority).
// 9. Moving a card to the FreeCell area to separate incorrect sequences.
// 10. Moving a single card to the FreeCell area (starting with the lowest rank).
// 11. Moving cards to the FreeCell area to separate correct sequences, starting with the lowest values.
// 12. Splitting sequences to create a larger sequence.
// 13. No hint popup
type MoveOption = {
    moveGroup: Card[];
    targetLength: number;
    targetColumn: GameBoardPlace;
    fromColumn: GameBoardPlace;
    condition: number;
};

export function findFreeCellHint(game: SolitaireGame, lastPriorityCheck: number, hintCards: string[]) {
    let hints = [] as HintItem[];

    for (let i = lastPriorityCheck; i < 13; i++) {
        if (i === 0) continue;
        switch (i) {
            case 1:
                hints = checkHint1(game, hintCards);
                break;
            case 2:
                hints = checkHint2(game, hintCards);
                break;
            case 3:
                hints = checkHint3(game, hintCards);
                break;
            case 4:
                hints = checkHint4(game, hintCards);
                break;
            case 5:
                hints = checkHint5(game, hintCards);
                break;
            case 6:
                hints = checkHint6(game, hintCards);
                break;
            case 7:
                hints = checkHint7(game, hintCards);
                break;
            case 8:
                hints = checkHint8(game, hintCards);
                break;
            case 9:
                hints = checkHint9(game, hintCards);
                break;
            case 10:
                hints = checkHint10(game, hintCards);
                break;
            case 11:
                //11. Moving cards to the FreeCell area to separate correct sequences, starting with the lowest values
                hints = checkHint11(game, hintCards);
                break;
            case 12:
                hints = checkHint12(game, hintCards);
                if (!hints.length && lastPriorityCheck > 0) {
                    hints = [new HintItem([], game.columns[0], HintType.RESET, 12)];
                }

                break;
        }

        if (hints.length) {
            return hints;
        }
    }
    return hints;
}
function sortByLowestValueCards(cards: Card[]) {
    cards.sort((a, b) => {
        if (a.value === b.value) {
            return a.place.value > b.place.value ? 1 : -1;
        } else {
            return a.value > b.value ? 1 : -1;
        }
    });
}

function sortOptionsByBiggestGroup(option: MoveOption[]) {
    option.sort((a, b) => {
        if (a.moveGroup.length === b.moveGroup.length) {
            return a.moveGroup[0].place.value > b.moveGroup[0].place.value ? 1 : -1;
        } else {
            return a.moveGroup.length < b.moveGroup.length ? 1 : -1;
        }
    });
}

function sortOptionsBySmallestCard(option: MoveOption[]) {
    option.sort((a, b) => {
        if (a.moveGroup[0].value === b.moveGroup[0].value) {
            return a.moveGroup[0].place.value > b.moveGroup[0].place.value ? 1 : -1;
        } else {
            return a.moveGroup[0].value < b.moveGroup[0].value ? 1 : -1;
        }
    });
}

function getLastCards(column: GameBoardPlace[]): Card[] {
    const sortedCards: Card[] = column.map((column) => column.lastCard).filter((card) => card !== null);
    return sortedCards;
}
function getEmptyPlaces(column: GameBoardPlace[]): GameBoardPlace[] {
    return column.filter((cell) => cell.isEmpty);
}

function checkHint1(game: SolitaireGame, hintCards: string[]) {
    // 1. Moving Cards to the Foundation (starting with the lowest-ranked card if other options are available).

    // from left to right columns then waste pile
    const lastColumnCards = getLastCards(game.columns);
    sortByLowestValueCards(lastColumnCards);

    for (const card of lastColumnCards) {
        if (hintCards.includes(card._id)) continue;
        const validFoundation = game.foundations.find((foundation) => foundation.canDropCard([card]));
        if (validFoundation) {
            return [new HintItem([card], validFoundation, HintType.MOVE, 1)];
        }
    }
    return [];
}
function checkHint2(game: SolitaireGame, hintCards: string[]) {
    // 2. Moving cards on the tableau to reveal cards that can be transferred to the Foundation.
    const foundationValues = game.foundations.map((foundation) =>
        foundation.lastCard ? foundation.lastCard.value : 1
    );
    foundationValues.sort((a, b) => a - b);
    const foundationLimit = foundationValues[0] + 2;
    const freeCells = getEmptyPlaces(game.freeCells);
    const groupLimit = freeCells.length + 2; //add two extra spot one for the last card that will go to the foundation and one move that must go to the tableau

    const lastMoveGroups = game.columns.map((column) => {
        const moveCards = column.getLastMoveGroup();
        if (moveCards.length < groupLimit && column.length > moveCards.length) {
            moveCards.unshift(column.cards[column.length - moveCards.length - 1]);
        }
        while (moveCards.length > groupLimit) {
            moveCards.shift();
        }
        return moveCards;
    });
    // lastMoveGroups - group of cards that could be potentially moved to the foundation
    // find if any of the cards can be moved to the foundation
    for (const moveGroup of lastMoveGroups) {
        let moveCards = [] as Card[];

        moveGroup.reverse();

        for (const card of moveGroup) {
            const canBeMovedToFoundation = game.foundations.find((foundation) => foundation.canDropCard([card]));

            if (canBeMovedToFoundation && moveCards.length) {
                if (card.value > foundationLimit) continue;

                const moveGroupValidOrder = [...moveCards].reverse();
                const canBeMovedToTableau = game.columns.filter((column) => column.canDropCards(moveGroupValidOrder));

                if (canBeMovedToTableau.length && !hintCards.includes(moveCards[moveCards.length - 1]._id)) {
                    canBeMovedToTableau.sort((a, b) => {
                        return b.length - a.length;
                    });
                    return [
                        checkIfTargetGroupCanBeMovedToOtherColumn(
                            new HintItem(moveGroupValidOrder, canBeMovedToTableau[0], HintType.MOVE, 2),
                            game,
                            hintCards
                        ),
                    ];
                }
            }
            moveCards.push(card);
        }
    }
    const columnToFoundation = game.columns.find((column) => {
        if (column.length > 1) {
            const foundationCard = column.cards[column.length - 2];
            if (foundationCard.value === ACES_VALUE) return true;

            if (foundationCard.value > foundationLimit) return false;
            return game.foundations.find((foundation) => foundation.canDropCard([foundationCard]));
        }
        return false;
    });
    if (freeCells.length && columnToFoundation && columnToFoundation.lastCard) {
        if (!hintCards.includes(columnToFoundation.lastCard._id))
            return [
                checkIfTargetGroupCanBeMovedToOtherColumn(
                    new HintItem([columnToFoundation.lastCard], freeCells[0], HintType.MOVE, 2, 1),
                    game,
                    hintCards
                ),
            ];
    }
    return [];
}
function checkIfTargetGroupCanBeMovedToOtherColumn(hintItem: HintItem, game: SolitaireGame, hintCards: string[]) {
    const freeCells = getEmptyPlaces(game.freeCells);
    const targetColumn = hintItem.to as Column;
    const lastGroup = targetColumn.getLastMoveGroup();
    const groupLimit = freeCells.length + 1;
    if (lastGroup.length && lastGroup.length < groupLimit) {
        if (!hintCards.includes(lastGroup[0]._id)) {
            const targetColumn = game.columns.find((column) => {
                if (column.length === 0) return false;
                return column.canDropCards(lastGroup);
            });
            if (targetColumn) {
                return checkIfTargetGroupCanBeMovedToOtherColumn(
                    new HintItem(lastGroup, targetColumn, hintItem.type, hintItem.priority),
                    game,
                    hintCards
                );
            }
        }
    }
    return hintItem;
}
function checkHint3(game: SolitaireGame, hintCards: string[]) {
    // 3. Transfer cards to the correct sequence (a sequence that is uninterrupted and can be moved as a whole). Prioritize forming larger sequences.
    const moveOptions = [] as MoveOption[];
    const freeCells = getEmptyPlaces(game.freeCells);
    const groupLimit = freeCells.length + 1; //add one extra spot one move that will go to the tableau
    const lastMoveGroups = game.columns.map((column) => {
        const moveCards = column.getLastMoveGroup();
        while (moveCards.length > groupLimit) {
            moveCards.shift();
        }
        return moveCards;
    });

    const correctColumns = game.columns
        .filter((column) => !column.getNextMovingGroup().length)
        .filter((column) => !!column.length);

    // first check if we can move a whole correct sequences to other columns
    for (const column of correctColumns) {
        const moveGroup = column.getLastMoveGroup();
        if (moveGroup.length > groupLimit - 1) {
            continue;
        }
        if (hintCards.includes(moveGroup[0]._id)) continue;
        const moveToColumn = game.columns.find((c) => {
            if (c.lastCard && hintCards.includes(c.lastCard._id)) return false;
            return c.length > 0 && c.canDropCards(moveGroup);
        });
        if (moveToColumn) {
            return [
                checkIfTargetGroupCanBeMovedToOtherColumn(
                    new HintItem(moveGroup, moveToColumn, HintType.MOVE, 3),
                    game,
                    hintCards
                ),
            ];
        }
    }

    if (!lastMoveGroups.length) return [];
    // lastMoveGroups - group of cards that could be potentially moved to the other column
    // find if any of the cards can be moved to the column
    for (const moveGroup of lastMoveGroups) {
        moveGroup.reverse();

        let moveCards = [] as Card[];

        for (let i = 0; i < moveGroup.length; i++) {
            const card = moveGroup[i];
            const canBeMovedToColumn = correctColumns.find((column) => {
                // if (column.canDropCards([card])) {
                //     const newLength = column.getLastMoveGroup().length + i + 1;
                //     if (newLength <= moveGroup.length) return false;
                // }
                return column.canDropCards([card]);
            });
            moveCards.push(card);

            if (canBeMovedToColumn) {
                if (!hintCards.includes(card._id)) {
                    if (moveCards.length <= groupLimit) {
                        const fromColumn = game.columns[card.place.value];

                        moveOptions.push({
                            moveGroup: [...moveCards].reverse(),
                            fromColumn,
                            targetColumn: canBeMovedToColumn,
                            targetLength: moveGroup.length + canBeMovedToColumn.getLastMoveGroup().length,
                            condition: 0,
                        });
                    }
                }
            }
        }
    }
    if (moveOptions.length) {
        sortOptionsByBiggestGroup(moveOptions);
        const hintOption = moveOptions[0];
        if (hintOption.targetColumn.getLastMoveGroup) {
            const lastMovingGroup = hintOption.targetColumn.getLastMoveGroup();
            const firstCard = lastMovingGroup[0];
            const moveGroupToColumn = game.columns.find((column) => {
                if (column.lastCard) {
                    if (hintCards.includes(firstCard._id)) return false;
                    const lastCard = column.lastCard;
                    if (lastCard.canDropInColumn(firstCard)) {
                        return true;
                    }
                }
                return false;
            });
            if (moveGroupToColumn && lastMovingGroup.length <= groupLimit) {
                return [
                    checkIfTargetGroupCanBeMovedToOtherColumn(
                        new HintItem(lastMovingGroup, moveGroupToColumn, HintType.MOVE, 3, hintOption.condition),
                        game,
                        hintCards
                    ),
                ];
            }
        }
        if (hintOption.fromColumn.getNextMovingGroup().length > 0) {
            return [
                checkIfTargetGroupCanBeMovedToOtherColumn(
                    new HintItem(hintOption.moveGroup, hintOption.targetColumn, HintType.MOVE, 3, hintOption.condition),
                    game,
                    hintCards
                ),
            ];
        }
    }
    return [];
}
function checkHint4(game: SolitaireGame, hintCards: string[]) {
    const moveOptions = [] as MoveOption[];
    game.freeCells.find((cell: GameBoardPlace) => {
        if (cell.lastCard) {
            if (!hintCards.includes(cell.lastCard._id)) {
                for (let col = 0; col < game.columns.length; col++) {
                    const column = game.columns[col];
                    if (column.length && column.canDropCards([cell.lastCard])) {
                        moveOptions.push({
                            moveGroup: [cell.lastCard],
                            fromColumn: cell,
                            targetColumn: column,
                            targetLength: column.getLastMoveGroup().length,
                            condition: 0,
                        });
                    }
                }
            }
        }
    });
    if (moveOptions.length) {
        sortOptionsByBiggestGroup(moveOptions);
        const hintOption = moveOptions[moveOptions.length - 1];
        return [
            checkIfTargetGroupCanBeMovedToOtherColumn(
                new HintItem(hintOption.moveGroup, hintOption.targetColumn, HintType.MOVE, 4, hintOption.condition),
                game,
                hintCards
            ),
        ];
    }
    return [];
}
function checkHint5(game: SolitaireGame, hintCards: string[]) {
    // 5. Creating smaller sequences (starting with those that will form smaller sequences).
    // Do not destroy the correct sequences. // -> the whole columns in correct order

    const moveOptions = [] as MoveOption[];
    const freeCells = getEmptyPlaces(game.freeCells);
    const groupLimit = freeCells.length + 1; //add one extra spot one move that will go to the tableau (limit if move at the same card)
    //
    const lastMoveGroups = game.columns
        .filter((column) => column.getNextMovingGroup().length)
        .map((column) => {
            const moveCards = column.getLastMoveGroup();
            while (moveCards.length > groupLimit) {
                moveCards.shift();
            }
            return moveCards;
        });
    // lastMoveGroups - group of cards that could be potentially moved to the other column
    // find if any of the cards can be moved to the column

    for (const moveGroup of lastMoveGroups) {
        moveGroup.reverse();
        let moveCards = [] as Card[];

        for (const card of moveGroup) {
            const canBeMovedToColumn = game.columns.find((column) => column.length && column.canDropCards([card]));
            moveCards.push(card);
            if (canBeMovedToColumn) {
                if (!hintCards.includes(card._id)) {
                    const targetLength = moveCards.length + canBeMovedToColumn.getLastMoveGroup().length;
                    const fromColumn = game.columns[card.place.value];

                    if (targetLength > fromColumn.getLastMoveGroup().length)
                        moveOptions.push({
                            moveGroup: [...moveCards].reverse(),
                            fromColumn,
                            targetColumn: canBeMovedToColumn,
                            targetLength: moveGroup.length + canBeMovedToColumn.getLastMoveGroup().length,
                            condition: 0,
                        });
                }
            }
        }
    }

    if (moveOptions.length) {
        sortOptionsByBiggestGroup(moveOptions);
        const hintOption = moveOptions[0];
        return [
            checkIfTargetGroupCanBeMovedToOtherColumn(
                new HintItem(hintOption.moveGroup, hintOption.targetColumn, HintType.MOVE, 5, hintOption.condition),
                game,
                hintCards
            ),
        ];
    }
    return [];
}
function checkHint6(game: SolitaireGame, hintCards: string[]) {
    // 6. Moving cards to the placeholder to create a larger sequence on the next move (prioritizing smaller cards first).
    const moveOptions = [] as MoveOption[];
    const emptyFreeCells = getEmptyPlaces(game.freeCells);
    const placeholders = getEmptyPlaces(game.columns);
    const moveLengthLimit = emptyFreeCells.length + 1; //add one extra spot one move that will go to the tableau
    if (!placeholders.length) return [];

    for (const column of game.columns) {
        const moveCards = column.getLastMoveGroup();
        const nextCards = column.getNextMovingGroup();
        const sequenceLength = moveCards.length;

        if (!moveCards.length) continue;
        if (hintCards.includes(moveCards[0]._id)) continue;
        if (nextCards.length === 0) continue; //there is no cards to uncover
        if (moveCards.length > moveLengthLimit) continue; //don't split the sequence

        const checkCards = [...nextCards];

        //check if any column can accept any card of checkCards
        const testGroup = [] as Card[];
        const testMoveGroup = [] as Card[];
        checkCards.reverse();

        checkCards.forEach((card) => {
            testMoveGroup.unshift(card);
            const columns = game.columns.filter((column) => column.canDropCards(testMoveGroup));

            columns.forEach((targetColumn) => {
                if (sequenceLength >= targetColumn.getLastMoveGroup().length + column.getLastMoveGroup().length) return;

                moveOptions.push({
                    moveGroup: [...moveCards],
                    fromColumn: game.columns[card.place.value],
                    targetColumn: placeholders[0],
                    targetLength: sequenceLength + targetColumn.getLastMoveGroup().length,
                    condition: 0,
                });
            });

            const moveColumns = game.columns.filter((column) =>
                testMoveGroup[testMoveGroup.length - 1].canDropInColumn(card)
            );
            moveColumns.forEach((moveColumn) => {
                if (sequenceLength >= moveColumn.getLastMoveGroup().length + column.getLastMoveGroup().length) return;
                moveOptions.push({
                    moveGroup: [...moveCards],
                    fromColumn: game.columns[card.place.value],
                    targetColumn: placeholders[0],
                    targetLength: sequenceLength + moveColumn.getLastMoveGroup().length,
                    condition: 0,
                });
            });
            testGroup.unshift(card);
        });
    }

    if (moveOptions.length) {
        sortOptionsBySmallestCard(moveOptions);
        const hintOption = moveOptions[moveOptions.length - 1];
        return [new HintItem(hintOption.moveGroup, hintOption.targetColumn, HintType.MOVE, 6, hintOption.condition)];
    }
    return [];
}
function checkHint7(game: SolitaireGame, hintCards: string[]) {
    // 7. Moving cards to FreeCells to enable sequence creation. (Don't split sequences)
    const moveOptions = [] as MoveOption[];
    const emptyFreeCells = getEmptyPlaces(game.freeCells);
    const groupMoveLimit = emptyFreeCells.length + 1; //add one extra spot one move that will go to the tableau
    const placeholders = getEmptyPlaces(game.freeCells);
    if (!placeholders.length) return [];
    const columnToMove = game.columns.filter(
        (c) => c.getNextMovingGroup().length > 0 && c.getLastMoveGroup().length === 1
    );
    if (!columnToMove.length) return [];

    for (const column of columnToMove) {
        const moveCards = column.getLastMoveGroup();
        const sequenceLength =
            moveCards.length > emptyFreeCells.length
                ? column.getLastMoveGroup().length
                : column.getNextMovingGroup().length;
        if (moveCards.length > emptyFreeCells.length + 1) {
            continue;
        }
        const checkCards = [...moveCards];
        if (column.cards.length > checkCards.length) {
            checkCards.unshift(column.cards[column.cards.length - checkCards.length - 1]);
        }
        //check if any column can accept any card of checkCards
        const moveGroup = [] as Card[];
        const testMoveGroup = [] as Card[];
        checkCards.reverse();
        checkCards.forEach((card, i) => {
            if (i > 1) return;
            testMoveGroup.unshift(card);
            if (hintCards.includes(card._id)) return;

            const columns = game.columns.filter((column) => column.canDropCards(testMoveGroup));

            columns.forEach((targetColumn) => {
                if (!moveGroup.length) return;
                if (sequenceLength >= targetColumn.getLastMoveGroup().length + column.getLastMoveGroup().length) return;
                moveOptions.push({
                    moveGroup: [moveGroup[moveGroup.length - 1]],
                    fromColumn: game.columns[card.place.value],
                    targetColumn: placeholders[0],
                    targetLength: sequenceLength + targetColumn.getLastMoveGroup().length,
                    condition: 0,
                });
            });

            const moveColumns = game.columns.filter((column) =>
                testMoveGroup[testMoveGroup.length - 1].canDropInColumn(card)
            );
            moveColumns.forEach((moveColumn) => {
                if (!moveGroup.length) return;
                if (sequenceLength >= moveColumn.getLastMoveGroup().length + column.getLastMoveGroup().length) return;
                moveOptions.push({
                    moveGroup: [moveGroup[moveGroup.length - 1]],
                    fromColumn: game.columns[card.place.value],
                    targetColumn: placeholders[0],
                    targetLength: sequenceLength + moveColumn.getLastMoveGroup().length,
                    condition: 0,
                });
            });
            moveGroup.unshift(card);
        });
    }
    if (moveOptions.length) {
        sortOptionsBySmallestCard(moveOptions);
        const hintOption = moveOptions[moveOptions.length - 1];
        return [new HintItem(hintOption.moveGroup, hintOption.targetColumn, HintType.MOVE, 7, hintOption.condition)];
    }
    return [];
}
function checkHint8(game: SolitaireGame, hintCards: string[]) {
    // 8. Filling an empty column (starting with the largest card from left to right, prioritizing FreeCells first and considering the number of cards in the stack — the larger the stack, the higher the priority).
    const emptyFreeCells = getEmptyPlaces(game.freeCells);
    const freeCells = game.freeCells
        .map((c) => c.lastCard)
        .filter((c) => !!c)
        .filter((c) => !hintCards.includes(c._id));
    const emptyColumns = getEmptyPlaces(game.columns);
    if (!emptyColumns.length) return [];

    sortByLowestValueCards(freeCells);
    freeCells.reverse();

    if (freeCells.length) {
        let moveGroups = [[freeCells[0]]] as Card[][];
        let lastCard = freeCells[0];
        for (let i = 1; i < freeCells.length; i++) {
            const card = freeCells[i];
            if (lastCard.canDropInColumn(card)) {
                moveGroups[moveGroups.length - 1].push(card);
            } else {
                moveGroups.push([card]);
            }
            lastCard = card;
        }
        moveGroups.sort((a, b) => {
            return b.length - a.length;
        });
        return [new HintItem([moveGroups[0][0]], emptyColumns[0], HintType.MOVE, 8)];
    }

    const columns = game.columns
        .filter((c) => c.getNextMovingGroup().length > 0)
        .map((c) => c.getLastMoveGroup())
        .filter((c: Card[]) => !!c.length)
        .filter((c: Card[]) => {
            return c.length < emptyFreeCells.length + 2;
        })
        .filter((c: Card[]) => !hintCards.includes(c[0]._id));
    columns.sort((a, b) => {
        if (b.length === a.length) {
            return b[0].index < a[0].index ? -1 : 1;
        }

        if (a.length > b.length) {
            return -2;
        } else if (a.length < b.length) {
            return 2;
        }
    });
    if (columns.length) {
        return [new HintItem(columns[0], emptyColumns[0], HintType.MOVE, 8)];
    }

    return [];
}
function checkHint9(game: SolitaireGame, hintCards: string[]) {
    // 9. Moving a card to the FreeCell area to separate incorrect sequences.
    const freeCells = getEmptyPlaces(game.freeCells);
    if (!freeCells.length) return [];
    const oneMoveGroupsColumn = game.columns.find(
        (c) =>
            c.lastCard &&
            c.getLastMoveGroup().length === 1 &&
            c.getNextMovingGroup().length > 0 &&
            !hintCards.includes(c.lastCard._id)
    );
    if (oneMoveGroupsColumn) {
        return [new HintItem([oneMoveGroupsColumn.getLastMoveGroup()[0]], freeCells[0], HintType.MOVE, 9)];
    }
    return [];
}
function checkHint10(game: SolitaireGame, hintCards: string[]) {
    //10. Movin
    const freeCells = getEmptyPlaces(game.freeCells);
    if (!freeCells.length) return [];

    const oneMoveGroupsColumn = game.columns.filter(
        (c) => c.lastCard && c.length === 1 && !hintCards.includes(c.lastCard._id)
    );
    if (oneMoveGroupsColumn.length) {
        let moveCard = oneMoveGroupsColumn[0].getLastMoveGroup()[0];
        oneMoveGroupsColumn.forEach((column) => {
            if (column.lastCard && column.lastCard.value < moveCard.value) {
                moveCard = column.getLastMoveGroup()[0];
            }
        });
        return [new HintItem([moveCard], freeCells[0], HintType.MOVE, 10)];
    }
    return [];
}
function checkHint11(game: SolitaireGame, hintCards: string[]) {
    // 11. Moving cards to the FreeCell area to separate correct sequences, starting with the lowest values
    const freeCells = getEmptyPlaces(game.freeCells);
    if (!freeCells.length) return [];
    const correctGroups = game.columns.filter(
        (c) => c.lastCard && c.getLastMoveGroup().length > 1 && !hintCards.includes(c.lastCard._id)
    );
    if (correctGroups.length && correctGroups[0].lastCard) {
        let moveCard = correctGroups[0].lastCard;

        correctGroups.forEach((column) => {
            if (column.lastCard && column.lastCard.value < moveCard.value) {
                moveCard = column.lastCard;
            }
        });
        return [new HintItem([moveCard], freeCells[0], HintType.MOVE, 11)];
    }
    return [];
}
function checkHint12(game: SolitaireGame, hintCards: string[]) {
    const freeCells = getEmptyPlaces(game.freeCells);

    const groupLimit = freeCells.length + 1;
    const correctSequences = game.columns.filter(
        (c) =>
            c.lastCard &&
            c.getLastMoveGroup().length > 0 &&
            c.getNextMovingGroup().length === 0 &&
            !hintCards.includes(c.lastCard._id)
    );
    for (let index = 0; index < correctSequences.length; index++) {
        const correctColumn = correctSequences[index];
        const moveGroup = correctColumn.getLastMoveGroup();
        while (moveGroup.length > groupLimit) {
            moveGroup.shift();
        }
        const drop = correctSequences.find((column) => column.canDropCards(moveGroup));
        if (drop) {
            return [new HintItem(moveGroup, drop, HintType.MOVE, 12)];
        }
    }
    return [];
}
